import React, { Fragment } from 'react';
import { Icon, Checkbox } from 'semantic-ui-react';
import classNames from 'classnames';
import _ from 'lodash';
import shortid from 'shortid';
import moment from 'moment';

import {
  ApplicationFieldEnum as ApplicationFields,
  ScannedApplicationFieldEnum as ScannedAppFields,
  StudentFieldEnum as StudentFields,
} from 'Common/FieldEnum';
import * as ManageAppHelpers from 'Pages/manageapplication/helpers/Helpers';
import { getHouseholdExceptionString } from 'Pages/manageapplication/helpers/HouseholdExceptionHelpers';
import { DateCell } from 'Common/GridHelper';
import { formatFrequency } from 'Pages/manageapplication/helpers/hub';
import { extractTruthyKeys } from 'Common/FormHelper';
import { DATE_FORMAT_FOR_SERVER } from 'Common/Constants';

import { CustomMemberFields, FHMRZToHMRCategory } from './Constants';
import {
  IncomingApplicationReasonEnum,
  CASE_NUMBER_REASON,
} from '../components/Constants';

export const convertScannedStudentToAppStudent = (
  scannedStudent,
  scannedApp,
  existingStudentsNotOnAppMap,
  languageId
) => {
  const hmr = scannedStudent[ScannedAppFields.HMR];
  const exceptions = {
    [ApplicationFields.CaseNumber]:
      scannedStudent[ScannedAppFields.MemberCaseNumber],
    [ApplicationFields.CaseTypeId]:
      scannedStudent[ScannedAppFields.CaseTypeId] || 0,
    [ApplicationFields.IsDirectCertified]: false,
    [ApplicationFields.IsFoster]: scannedStudent[ScannedAppFields.Foster],
    [ApplicationFields.IsKinGap]: scannedStudent[ScannedAppFields.KinGap],
    [ApplicationFields.IsEmancipatedMinor]: false,
    [ApplicationFields.IsExtendedBenefits]: false,
    [ApplicationFields.IsExtendedBenefitsHasChanged]: false,
    [ApplicationFields.IsHeadStart]: false,
    [ApplicationFields.HMRCategory]: FHMRZToHMRCategory[hmr] || 0,
  };

  const income1 = scannedStudent[ScannedAppFields.MemberIncome1];
  const income2 = scannedStudent[ScannedAppFields.MemberIncome2];
  const income3 = scannedStudent[ScannedAppFields.MemberIncome3];
  const income4 = scannedStudent[ScannedAppFields.MemberIncome4];

  const incomeObj = {
    [ApplicationFields.Freq1]: getIncomeFrequency(
      income1,
      scannedStudent[ScannedAppFields.Freq1]
    ),
    [ApplicationFields.Freq2]: getIncomeFrequency(
      income2,
      scannedStudent[ScannedAppFields.Freq2]
    ),
    [ApplicationFields.Freq3]: getIncomeFrequency(
      income3,
      scannedStudent[ScannedAppFields.Freq3]
    ),
    [ApplicationFields.Freq4]: getIncomeFrequency(income4, [
      ScannedAppFields.Freq4,
    ]),
    [ApplicationFields.Income1]: income1 || 0,
    [ApplicationFields.Income2]: income2 || 0,
    [ApplicationFields.Income3]: income3 || 0,
    [ApplicationFields.Income4]: income4 || 0,
  };

  const firstName = scannedStudent[ScannedAppFields.MemberFirstName];
  const lastName = scannedStudent[ScannedAppFields.MemberLastName];
  let siteId = scannedStudent[ScannedAppFields.MemberSchoolNumber];
  const studentId = scannedStudent[ScannedAppFields.MemberStudentNumber];
  let idSeed = 0;

  const existingStudentNotOnApp = existingStudentsNotOnAppMap[studentId];
  if (existingStudentNotOnApp) {
    idSeed = existingStudentNotOnApp[StudentFields.IdSeed];
  }

  return {
    [ApplicationFields.BirthDate]: scannedStudent[ScannedAppFields.BirthDateDT],
    [ApplicationFields.CaseTypeId]:
      scannedStudent[ScannedAppFields.CaseTypeId] || 0,
    [ApplicationFields.CaseNumber]:
      scannedStudent[ScannedAppFields.MemberCaseNumber],
    [ApplicationFields.SiteId]: siteId,
    [ApplicationFields.FirstName]: firstName,
    [ApplicationFields.MiddleName]: null,
    [ApplicationFields.LastName]: lastName,
    [ApplicationFields.ServingNumber]: studentId,
    [ApplicationFields.DeniedReason]: '',
    [ApplicationFields.Exceptions]: exceptions,
    [ApplicationFields.EffectiveDate]: null,
    [ApplicationFields.EffectiveDateHasChanged]: false,
    [ApplicationFields.EthnicityId]: 0,
    [ApplicationFields.GradeId]: scannedStudent[ScannedAppFields.MemberGradeId],
    [ApplicationFields.Id]: studentId,
    [ApplicationFields.IdSeed]: idSeed,
    [ApplicationFields.RaceId]: scannedApp[ScannedAppFields.RaceId],
    [ApplicationFields.IsNewToApp]: true,
    [ApplicationFields.RaceId]: null,
    [ApplicationFields.Income]: incomeObj,
    [ApplicationFields.StatusBeforeVerification]: null,
    [ApplicationFields.StatusChangeType]: 0,
    [ApplicationFields.StatusCurrent]: null,
    [ApplicationFields.StatusFuture]: '',
    [ApplicationFields.Teacher]: null,
    [ApplicationFields.LanguageId]: languageId,
  };
};

const getCellProps = (fieldName) => (state, rowInfo, column) => {
  if (rowInfo && rowInfo.original) {
    const diffs = rowInfo.original[CustomMemberFields.DifferingProperties];

    if (diffs && _.get(diffs, fieldName)) {
      return {
        style: {
          backgroundColor: '#fad97d',
        },
      };
    }
  }

  return {};
};

export const createHouseholdTableColumns = (
  setRowChecked,
  checkedRows,
  caseTypes
) => {
  let columns = [];

  if (setRowChecked && checkedRows) {
    columns.push({
      id: 'Checkbox',
      sortable: false,
      resizable: false,
      width: 35,
      style: { textAlign: 'center' },
      Header: (props) => {
        const rowIndices = Object.keys(checkedRows);

        const allChecked =
          rowIndices.length > 0 && rowIndices.every((r) => checkedRows[r]);

        const onChange = () => {
          if (!allChecked) {
            rowIndices.forEach((i) => {
              if (!checkedRows[i]) {
                setRowChecked(i, true);
              }
            });
          } else {
            rowIndices.forEach((i) => {
              setRowChecked(i, false);
            });
          }
        };

        return (
          <Checkbox
            style={{ marginTop: 12 }}
            checked={allChecked}
            onChange={onChange}
          />
        );
      },
      Cell: (props) => {
        const onChange = () => {
          setRowChecked(props.index, !checkedRows[props.index]);
        };

        return (
          <Checkbox onChange={onChange} checked={checkedRows[props.index]} />
        );
      },
    });
  }

  columns = columns.concat([
    {
      id: 'Icon',
      sortable: false,
      resizable: false,
      width: 35,
      style: { textAlign: 'center' },
      Cell: (props) => {
        const { memberData } = props;

        const emancipatedStudents = memberData
          ? memberData.filter(
              (y) => y.Exceptions && y.Exceptions.IsEmancipatedMinor
            )
          : null;

        return (
          <Fragment>
            <Icon
              color={
                emancipatedStudents &&
                emancipatedStudents.find((x) => x.Id === props.original.Id)
                  ? 'blue'
                  : 'black'
              }
              name={ManageAppHelpers.getIconNameFromId(props.original.Id)}
            />
          </Fragment>
        );
      },
    },
    {
      Header: 'Name',
      sortable: true,
      width: 200,
      Cell: (props) => {
        return (
          <Fragment>
            {props.original.FirstName} {props.original.LastName}
          </Fragment>
        );
      },
      getProps: (state, rowInfo, column) => {
        return {
          ...getCellProps(ApplicationFields.FirstName)(state, rowInfo, column),
          ...getCellProps(ApplicationFields.LastName)(state, rowInfo, column),
        };
      },
    },
    {
      Header: 'Student #',
      width: 110,
      accessor: ApplicationFields.Id,
      sortable: true,
      Cell: (props) => {
        return (
          <Fragment>
            {ManageAppHelpers.getMemberType(props.original.Id) ===
            ManageAppHelpers.MemberTypeEnum.Student
              ? props.original.Id
              : null}
          </Fragment>
        );
      },
    },
    {
      Header: 'Grade',
      width: 50,
      accessor: ApplicationFields.GradeId,
      sortable: true,
      getProps: getCellProps(ApplicationFields.GradeId),
    },
    {
      Header: 'Site',
      width: 80,
      accessor: ApplicationFields.SiteId,
      sortable: true,
      getProps: getCellProps(ApplicationFields.SiteId),
    },
    {
      Header: 'Effective Date',
      width: 110,
      accessor: ApplicationFields.EffectiveDate,
      sortable: true,
      Cell: DateCell,
      getProps: getCellProps(ApplicationFields.EffectiveDate),
    },
    {
      Header: 'Additional Info',
      accessor: ApplicationFields.AdditionalInfo,
      sortable: true,
      width: 175,
      Cell: (props) => {
        const { original: { Exceptions: exceptions } = {} } = props;
        const content =
          getHouseholdExceptionString(exceptions, caseTypes) || '';

        const containerClassName = classNames({
          'scroll-container': content.length > 30,
        });

        return (
          <div className={containerClassName}>
            <span className={'scroll-content'}>{content}</span>
          </div>
        );
      },
      getProps: getCellProps(ApplicationFields.Exceptions),
    },
    {
      Header: 'Address 1',
      accessor: ApplicationFields.Address1,
      width: 200,
      getProps: getCellProps(ApplicationFields.Address1),
    },
    {
      Header: 'Address 2',
      accessor: ApplicationFields.Address2,
      width: 200,
      getProps: getCellProps(ApplicationFields.Address2),
    },
    {
      Header: 'City',
      accessor: ApplicationFields.City,
      width: 90,
      getProps: getCellProps(ApplicationFields.City),
    },
    {
      Header: 'State',
      accessor: ApplicationFields.State,
      width: 60,
      getProps: getCellProps(ApplicationFields.State),
    },
    {
      Header: 'Zip',
      accessor: ApplicationFields.ZipCode,
      width: 90,
      getProps: getCellProps(ApplicationFields.ZipCode),
    },
    {
      Header: 'Salary',
      sortable: true,
      width: 140,
      Cell: (props) => {
        return (
          <Fragment>
            {formatFrequency(
              props.original.Income.Freq1,
              props.original.Income.Income1
            )}
          </Fragment>
        );
      },
      getProps: (state, rowInfo, column) => ({
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Freq1])(
          state,
          rowInfo,
          column
        ),
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Income1])(
          state,
          rowInfo,
          column
        ),
      }),
    },
    {
      Header: 'Welfare/Alimony/Child Support',
      sortable: true,
      width: 205,
      Cell: (props) => {
        return (
          <Fragment>
            {formatFrequency(
              props.original[ApplicationFields.Income][ApplicationFields.Freq2],
              props.original[ApplicationFields.Income][
                ApplicationFields.Income2
              ]
            )}
          </Fragment>
        );
      },
      getProps: (state, rowInfo, column) => ({
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Freq2])(
          state,
          rowInfo,
          column
        ),
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Income2])(
          state,
          rowInfo,
          column
        ),
      }),
    },
    {
      Header: 'Retirement/Pension/Social Security',
      sortable: true,
      width: 225,
      Cell: (props) => {
        return (
          <Fragment>
            {formatFrequency(
              props.original[ApplicationFields.Income][ApplicationFields.Freq3],
              props.original[ApplicationFields.Income][
                ApplicationFields.Income3
              ]
            )}
          </Fragment>
        );
      },
      getProps: (state, rowInfo, column) => ({
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Freq3])(
          state,
          rowInfo,
          column
        ),
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Income3])(
          state,
          rowInfo,
          column
        ),
      }),
    },
    {
      Header: 'Other',
      sortable: true,
      Cell: (props) => {
        return (
          <Fragment>
            {formatFrequency(
              props.original[ApplicationFields.Income][ApplicationFields.Freq4],
              props.original[ApplicationFields.Income][
                ApplicationFields.Income4
              ]
            )}
          </Fragment>
        );
      },
      getProps: (state, rowInfo, column) => ({
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Freq4])(
          state,
          rowInfo,
          column
        ),
        ...getCellProps([ApplicationFields.Income, ApplicationFields.Income4])(
          state,
          rowInfo,
          column
        ),
      }),
    },
  ]);

  return [
    {
      Header: <strong className={'text-orange'}>Household Members</strong>,
      columns,
    },
  ];
};

export const getApplicationMembers = (appMembers) => {
  const members = [];
  let hasAddedHeadOfHousehold = false;

  appMembers.forEach((m) => {
    const memberType = ManageAppHelpers.getMemberType(m[ApplicationFields.Id]);

    if (memberType === ManageAppHelpers.MemberTypeEnum.HOH) {
      if (hasAddedHeadOfHousehold) {
        members.push({
          ...m,
          [ApplicationFields.IsHOH]: false,
          [ApplicationFields.Id]: `member_${shortid.generate()}`,
        });
      } else {
        hasAddedHeadOfHousehold = true;
        members.push(m);
      }
    } else if (memberType === ManageAppHelpers.MemberTypeEnum.Member) {
      members.push(m);
    }
  });

  return members;
};

export const getApplicationStudents = (appMembers) => {
  return appMembers.filter(
    (m) =>
      ManageAppHelpers.getMemberType(m[ApplicationFields.Id]) ===
      ManageAppHelpers.MemberTypeEnum.Student
  );
};

export const createApplicationFromSelectedRows = (
  existingApp,
  existingMembers,
  incomingMembers,
  incomingHOH,
  existingStudents
) => {
  const resultingApp = _.cloneDeep(existingApp || {});
  _.set(
    resultingApp,
    [
      ApplicationFields.ApplicationInformation,
      ApplicationFields.ApplicationDate,
    ],
    moment().format(DATE_FORMAT_FOR_SERVER)
  );

  // Update site ids for students found existing application in case they don't match up
  const updatedIncomingMembers = incomingMembers.map((im) => {
    const matchingStudent = existingStudents.find(
      (s) => s[ApplicationFields.Id] === im[ApplicationFields.Id]
    );

    if (matchingStudent) {
      // Existing categorical information should be preserved, new information should just be added without replacing
      // existing info
      const existingExceptions = _.pickBy(
        matchingStudent[ApplicationFields.Exceptions],
        (value, key) => Boolean(value)
      );
      const {
        IsFoster: existingIsFoster,
        IsKinGap: existingIsKinGap,
        HMRCategory: existingHMR,
      } = existingExceptions;
      const {
        IsFoster: incomingIsFoster,
        IsKinGap: incomingIsKinGap,
        HMRCategory: incomingHMR,
        ...restOfIncomingExceptions
      } = im[ApplicationFields.Exceptions];

      const fixedHMR = existingHMR || incomingHMR;
      const fixedIncomingExceptions = {
        IsFoster: existingIsFoster || (!existingIsKinGap && incomingIsFoster), // Can't be Foster if it's already KinGAP
        IsKinGap: existingIsKinGap || (!existingIsFoster && incomingIsKinGap), // Can't be KinGAP if it's already Foster
        ...restOfIncomingExceptions,
      };

      return {
        ...im,
        [ApplicationFields.SiteId]: matchingStudent[ApplicationFields.SiteId],
        [ApplicationFields.Exceptions]: {
          ...fixedIncomingExceptions,
          ...existingExceptions,
          ...{ HMRCategory: fixedHMR },
        },
      };
    }
    return im;
  });

  const members = existingMembers.concat(updatedIncomingMembers);

  const isUsingIncomingHOH = members.find(
    (m) => m[ApplicationFields.IncomingHOHId] && m[ApplicationFields.IsHOH]
  );

  // Remove any custom UI fields added to the member objects
  const normalizedMembers = members.map((am) =>
    _.pickBy(am, (value, key) => !CustomMemberFields.hasOwnProperty(key))
  );

  resultingApp[ApplicationFields.Members] =
    getApplicationMembers(normalizedMembers);
  resultingApp[ApplicationFields.Students] =
    getApplicationStudents(normalizedMembers);

  if (isUsingIncomingHOH) {
    const incomingHOHContactInfo = {
      [ApplicationFields.Address1]:
        incomingHOH[ScannedAppFields.HouseholdAddress1],
      [ApplicationFields.Address2]:
        incomingHOH[ScannedAppFields.HouseholdAddress2],
      [ApplicationFields.City]: incomingHOH[ScannedAppFields.City],
      [ApplicationFields.EmailId]: incomingHOH[ScannedAppFields.EmailId],
      [ApplicationFields.HomePhone]:
        incomingHOH[ScannedAppFields.HouseholdPhone],
      [ApplicationFields.State]: incomingHOH[ScannedAppFields.State],
      [ApplicationFields.WorkPhone]:
        incomingHOH[ScannedAppFields.HouseholdWkPhone],
      [ApplicationFields.ZipCode]: incomingHOH[ScannedAppFields.Zip],
    };

    resultingApp[ApplicationFields.HouseholdContactInfo] =
      incomingHOHContactInfo;
  }

  return resultingApp;
};

export const createInitialResultingApplication = (existingApp) => {
  return {
    ...existingApp,
    [ApplicationFields.Members]: [],
    [ApplicationFields.Students]: [],
  };
};
export const getIncomeFrequency = (income, frequency) => {
  let incomeAmount = Number(income);

  if (!isNaN(incomeAmount) && incomeAmount > 0) {
    return frequency;
  }

  return -1;
};

export const EXTRA_HOH_FIELDS = [
  ApplicationFields.Address1,
  ApplicationFields.Address2,
  ApplicationFields.City,
  ApplicationFields.State,
  ApplicationFields.ZipCode,
];

export const convertScannedMemberToAppMember = (member) => {
  return {
    [ApplicationFields.EmancipatedStudentId]: null,
    [ApplicationFields.FirstName]: member[ScannedAppFields.MemberFirstName],
    [ApplicationFields.LastName]: member[ScannedAppFields.MemberLastName],
    [ApplicationFields.LanguageId]: null,
    [ApplicationFields.Id]: `member_${shortid.generate()}`,
    [ApplicationFields.SSN]: '',
    [ApplicationFields.IsHOH]: false,
    [ApplicationFields.Income]: {
      [ApplicationFields.Freq1]: getIncomeFrequency(
        member[ScannedAppFields.MemberIncome1],
        member[ScannedAppFields.Freq1]
      ),
      [ApplicationFields.Freq2]: getIncomeFrequency(
        member[ScannedAppFields.MemberIncome2],
        member[ScannedAppFields.Freq2]
      ),
      [ApplicationFields.Freq3]: getIncomeFrequency(
        member[ScannedAppFields.MemberIncome3],
        member[ScannedAppFields.Freq3]
      ),
      [ApplicationFields.Freq4]: getIncomeFrequency(
        member[ScannedAppFields.MemberIncome4],
        member[ScannedAppFields.Freq4]
      ),
      [ApplicationFields.Income1]: member[ScannedAppFields.MemberIncome1] || 0,
      [ApplicationFields.Income2]: member[ScannedAppFields.MemberIncome2] || 0,
      [ApplicationFields.Income3]: member[ScannedAppFields.MemberIncome3] || 0,
      [ApplicationFields.Income4]: member[ScannedAppFields.MemberIncome4] || 0,
    },
  };
};

export const isAlreadyOnApplication = ({
  member,
  existingCheckedRows,
  incomingCheckedRows,
  existingMembers,
  incomingMembers,
}) => {
  const selectedExistingMembers = extractTruthyKeys(existingCheckedRows).map(
    (i) => existingMembers[i]
  );
  const selectedIncomingMembers = extractTruthyKeys(incomingCheckedRows).map(
    (i) => incomingMembers[i]
  );

  const members = selectedExistingMembers.concat(selectedIncomingMembers);

  return members.some((m) => {
    const bothAreMembers =
      ManageAppHelpers.getMemberType(m[ApplicationFields.Id]) ===
        ManageAppHelpers.MemberTypeEnum.Member &&
      ManageAppHelpers.getMemberType(member[ApplicationFields.Id]) ===
        ManageAppHelpers.MemberTypeEnum.Member;
    const bothAreHOH =
      ManageAppHelpers.getMemberType(m[ApplicationFields.Id]) ===
        ManageAppHelpers.MemberTypeEnum.HOH &&
      ManageAppHelpers.getMemberType(member[ApplicationFields.Id]) ===
        ManageAppHelpers.MemberTypeEnum.HOH;

    // Member ids are generated on the frontend initially and then assigned a GUID by the backend so we can't
    // really use ids as a comparison for members
    if (bothAreMembers || bothAreHOH) {
      return (
        m[ApplicationFields.FirstName].toLowerCase().trim() ===
          member[ApplicationFields.FirstName].toLowerCase().trim() &&
        m[ApplicationFields.LastName].toLowerCase().trim() ===
          member[ApplicationFields.LastName].toLowerCase().trim()
      );
    }
    return m[ApplicationFields.Id] === member[ApplicationFields.Id];
  });
};

export const canStartNewApplication = (
  existingAppStudents,
  students,
  duplicateStudentMatches,
  selectedExistingMembers
) => {
  const hasExistingStudent = students.some((s) => {
    const match = duplicateStudentMatches.find((matchObj) => {
      const target = matchObj.Target;
      return (
        target[ApplicationFields.Id] === s[ApplicationFields.Id] &&
        target[ApplicationFields.Id] === s[ApplicationFields.Id]
      );
    });

    return match;
  });

  const hasExistingAppStudent = students.some((s) => {
    return existingAppStudents.some(
      (eas) => eas[ApplicationFields.Id] === s[ApplicationFields.Id]
    );
  });

  return (
    students.length > 0 &&
    !hasExistingStudent &&
    !hasExistingAppStudent &&
    selectedExistingMembers.length === 0
  );
};

export const getCreateOrEditErrorMsg = (application) => {
  const members = application[ApplicationFields.Members] || [];
  const students = application[ApplicationFields.Students] || [];

  const hasHOH = members.some((m) => m[ApplicationFields.IsHOH]);
  const hasStudents = students.length > 0;

  if (hasHOH && hasStudents) {
    return null;
  }

  if (!hasHOH && !hasStudents) {
    return 'Please add a Head of Household and a student to the Resulting Application.';
  } else if (!hasHOH) {
    return 'Please add a Head of Household to the Resulting Application.';
  } else {
    return 'Please add a Student to the Resulting Application.';
  }
};

export const getReasonText = (ReasonId) => {
  if (ReasonId === IncomingApplicationReasonEnum.CaseNumber) {
    return CASE_NUMBER_REASON;
  }

  return _.invert(IncomingApplicationReasonEnum)[ReasonId];
};



// WEBPACK FOOTER //
// ./src/pages/reviewincomingapplications/helpers/index.js